/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';

const POLL_MS = 20000;

const debug = (label, e) => {
  if (process.env.NODE_ENV !== 'production') console.debug(`[NotificationsBell] ${label}`, e);
};

/**
 * In-app notifications state: polling list + unread counter + read actions.
 * Polling pauses while the tab is hidden.
 */
export const useNotifications = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setItems(res.data.items || []);
      setUnread(res.data.unread_count || 0);
    } catch (e) {
      debug('load failed', e);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(() => { if (document.visibilityState === 'visible') load(); }, POLL_MS);
    return () => clearInterval(iv);
  }, [load]);

  const markAllRead = useCallback(async () => {
    try {
      await api.post('/notifications/read-all');
      setUnread(0);
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
    } catch (e) {
      debug('markAllRead failed', e);
    }
  }, []);

  const markRead = useCallback(async (n) => {
    try {
      await api.post(`/notifications/${n.id}/read`);
    } catch (e) {
      debug('mark-read failed', e);
    }
    setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
    setItems((prev) => prev.map((i) => (i.id === n.id ? { ...i, read: true } : i)));
  }, []);

  return { items, unread, markAllRead, markRead };
};
